/**
 * IB Dashboard Service
 * Provides mocked dashboard data for the Performance Overview.
 * Data varies by accountType (live/demo) and period (1m/3m/6m/1y).
 */

// Period multipliers — simulate how metrics grow over longer time windows
const periodFactors = {
  '1m': { volume: 1, trades: 1, engagement: 0, profit: 0, rank: 0 },
  '3m': { volume: 2.6, trades: 2.4, engagement: 5, profit: 3, rank: 0 },
  '6m': { volume: 4.8, trades: 4.1, engagement: 8, profit: 5, rank: 1 },
  '1y': { volume: 9.2, trades: 7.5, engagement: 12, profit: 8, rank: 2 }
}

const periodInsights = {
  live: {
    '1m': 'your traders increased their volume by 12% compared to last month.',
    '3m': 'live accounts grew 34% in volume over the last quarter, with BTC leading asset activity.',
    '6m': 'engagement among live traders improved by 18% in the past 6 months.',
    '1y': 'your community generated over 42,000 lots this year, a 27% increase year-over-year.'
  },
  demo: {
    '1m': 'demo accounts show a 23% increase in new trader onboarding this month.',
    '3m': 'demo trader retention improved by 15% over the last 3 months.',
    '6m': 'demo-to-live conversion rate reached 28% in the past 6 months.',
    '1y': 'over 340 demo traders transitioned to live accounts this year.'
  }
}

class DashboardService {

  getDashboardData(partnerName = 'Partner', accountType = 'live', period = '1m') {
    const f = periodFactors[period] || periodFactors['1m']

    if (accountType === 'demo') {
      return {
        kpis: this._buildKPIs(partnerName, 'demo', f),
        topProfitable: this._scaleTraders(this._baseDemoProfitable(), f, 'percentage'),
        topActive: this._scaleTraders(this._baseDemoActive(), f, 'trades'),
        topAssets: this._scaleTraders(this._baseDemoAssets(), f, 'trades'),
        insight: periodInsights.demo[period] || periodInsights.demo['1m']
      }
    }
    return {
      kpis: this._buildKPIs(partnerName, 'live', f),
      topProfitable: this._scaleTraders(this._baseLiveProfitable(), f, 'percentage'),
      topActive: this._scaleTraders(this._baseLiveActive(), f, 'trades'),
      topAssets: this._scaleTraders(this._baseLiveAssets(), f, 'trades'),
      insight: periodInsights.live[period] || periodInsights.live['1m']
    }
  }

  // --- KPI builders ---

  _buildKPIs(partnerName, type, f) {
    const base = type === 'demo'
      ? { engagement: 68, volume: 12840, profit: 61, rank: 5, volumeLabel: 'demo' }
      : { engagement: 45, volume: 4562, profit: 49, rank: 3, volumeLabel: 'live' }

    const engagement = Math.min(base.engagement + f.engagement, 99)
    const volume = Math.round(base.volume * f.volume)
    const profit = Math.min(base.profit + f.profit, 95)
    const rank = Math.max(base.rank - f.rank, 1)

    return [
      {
        label: 'Engagement Traders',
        value: `${engagement}%`,
        description: 'Active Traders / Total Traders',
        icon: 'fa-regular fa-handshake'
      },
      {
        label: `Total Volume (${base.volumeLabel} accounts)`,
        value: `${volume.toLocaleString()} Lots`,
        description: 'Lots',
        icon: 'fa-regular fa-chart-bar'
      },
      {
        label: 'Profitable Traders',
        value: `${profit}%`,
        description: 'Profit / Unprofit',
        icon: 'fa-regular fa-star'
      },
      {
        label: 'Ranking',
        value: `Top ${rank}`,
        description: `Your IB is top ${rank} communities of ${partnerName}`,
        icon: 'fa-regular fa-trophy-alt'
      }
    ]
  }

  // --- Scale helpers ---

  _scaleTraders(base, f, field) {
    return base.map(item => {
      if (field === 'percentage') {
        return { ...item, percentage: Math.min(item.percentage + f.profit, 98) }
      }
      return { ...item, trades: Math.round(item.trades * f.trades) }
    })
  }

  // --- Base data (1 month) ---

  _baseLiveProfitable() {
    return [
      { name: 'Oliver Thompson', percentage: 40 },
      { name: 'Sophia Martinez', percentage: 36 },
      { name: 'Liam Johnson', percentage: 30 },
      { name: 'Emma Williams', percentage: 29 },
      { name: 'Noah Brown', percentage: 22 }
    ]
  }

  _baseLiveActive() {
    return [
      { name: 'Oliver Thompson', trades: 2356 },
      { name: 'Sophia Martinez', trades: 2148 },
      { name: 'Liam Johnson', trades: 2065 },
      { name: 'Emma Williams', trades: 1954 },
      { name: 'Noah Brown', trades: 1542 }
    ]
  }

  _baseLiveAssets() {
    return [
      { name: 'BTC', trades: 168 },
      { name: 'BNB', trades: 162 },
      { name: 'NVDA', trades: 158 },
      { name: 'XAU/USD', trades: 154 },
      { name: 'USOIL', trades: 145 }
    ]
  }

  _baseDemoProfitable() {
    return [
      { name: 'James Carter', percentage: 72 },
      { name: 'Isabella Davis', percentage: 65 },
      { name: 'Ethan Moore', percentage: 58 },
      { name: 'Ava Wilson', percentage: 51 },
      { name: 'Lucas Taylor', percentage: 44 }
    ]
  }

  _baseDemoActive() {
    return [
      { name: 'James Carter', trades: 4120 },
      { name: 'Isabella Davis', trades: 3890 },
      { name: 'Ethan Moore', trades: 3245 },
      { name: 'Ava Wilson', trades: 2980 },
      { name: 'Lucas Taylor', trades: 2510 }
    ]
  }

  _baseDemoAssets() {
    return [
      { name: 'EUR/USD', trades: 342 },
      { name: 'GBP/USD', trades: 298 },
      { name: 'XAU/USD', trades: 275 },
      { name: 'US30', trades: 231 },
      { name: 'NAS100', trades: 198 }
    ]
  }
}

export default new DashboardService()
